import { NetSuiteRecordCurrency as GeneratedNetSuiteRecordCurrency } from "@redotech/cockroachdb-codegen/congodb/net-suite-record-currency";
import { createEnum } from "@redotech/zod-util/zod-enum";
import {
  ALLOWED_DYNAMIC_VARIABLES_ON_RMA_BODY_LEVEL,
  type RmaBodyLevelVariableMap,
} from "../dynamic-variables/rma-body-level";

export enum NetsuiteCloseReturnAuthorizationPermissionType {
  CLOSE = "close",
  AUTOMATION = "automation",
}

/**
 * Which currency the amounts on records we create/adjust (return authorization,
 * credit memo, cash refund) should be represented in. We only act when the
 * originating NetSuite transaction's currency matches the selected source;
 * otherwise we leave amounts untouched. Absent = feature off.
 *
 * Values are sourced from the generated `net_suite_record_currency` CockroachDB
 * enum so they cannot drift from the schema; the keys are local aliases kept for
 * existing callers. See `docs/db-enums.md`.
 */
export const NetSuiteRecordCurrency = {
  SHOP: GeneratedNetSuiteRecordCurrency.Shop,
  PRESENTMENT: GeneratedNetSuiteRecordCurrency.Presentment,
} as const;
export type NetSuiteRecordCurrency =
  (typeof NetSuiteRecordCurrency)[keyof typeof NetSuiteRecordCurrency];

export const NetSuiteRecordCurrencyZodSchema = createEnum(
  NetSuiteRecordCurrency,
  { enumName: "NetSuiteRecordCurrency" },
);

export const NetsuiteCreditMemoSources = ["rma", "invoice"] as const;
export type NetsuiteCreditMemoSource =
  (typeof NetsuiteCreditMemoSources)[number];
export const NetsuiteCreditMemoSource = NetsuiteCreditMemoSources;

export const NetSuiteItemReceiptRestockDecisionSource = {
  NETSUITE_ITEM_RECEIPT_RESTOCK_FIELD: "netsuite_item_receipt_restock_field",
  REDO_INVENTORY_RESTOCK_SETTING: "redo_inventory_restock_setting",
} as const;
export type NetSuiteItemReceiptRestockDecisionSource =
  (typeof NetSuiteItemReceiptRestockDecisionSource)[keyof typeof NetSuiteItemReceiptRestockDecisionSource];
export const NetSuiteItemReceiptRestockDecisionSources = [
  NetSuiteItemReceiptRestockDecisionSource.NETSUITE_ITEM_RECEIPT_RESTOCK_FIELD,
  NetSuiteItemReceiptRestockDecisionSource.REDO_INVENTORY_RESTOCK_SETTING,
] as const;
export const NetSuiteItemReceiptRestockDecisionSourceZodSchema = createEnum(
  NetSuiteItemReceiptRestockDecisionSource,
  { enumName: "NetSuiteItemReceiptRestockDecisionSource" },
);

export type FieldValueType = "string" | "number" | "boolean";

export interface NetSuiteCustomField {
  fieldKey: string;
  fieldValue: string | NetSuiteCustomField[];
  valueType?: FieldValueType;
}

const BOOLEAN_DYNAMIC_VARIABLES_ON_NETSUITE_RMA_BODY_LEVEL = [
  "isPosReturn",
  "hasDamagedItems",
] as const;

export const ALLOWED_DYNAMIC_VARIABLES_ON_NETSUITE_RMA_BODY_LEVEL = [
  ...ALLOWED_DYNAMIC_VARIABLES_ON_RMA_BODY_LEVEL,
  ...BOOLEAN_DYNAMIC_VARIABLES_ON_NETSUITE_RMA_BODY_LEVEL,
] as const;

export type NetSuiteRmaBodyLevelVariableMap = RmaBodyLevelVariableMap &
  Record<
    (typeof BOOLEAN_DYNAMIC_VARIABLES_ON_NETSUITE_RMA_BODY_LEVEL)[number],
    boolean
  >;

export const ALLOWED_DYNAMIC_VARIABLES_ON_LINE_ITEM = [
  "primaryReturnReason",
  "secondaryReturnReason",
  "primaryReasonCode",
  "secondaryReasonCode",
  "isGreenReturn",
  "compensationType",
  "refundAmount",
  "storeCreditAmount",
  "lineItemId",
  "comments",
  "trackingNumber",
  "carrierName",
] as const;

export const ALLOWED_DYNAMIC_VARIABLES_ON_CREDIT_MEMO = [
  "ecommerceGiftCardId",
] as const;

export const ALLOWED_DYNAMIC_VARIABLES_ON_CASH_REFUND = [
  "ecommerceGiftCardId",
  "orderId",
  "upsellStripeChargeId",
] as const;

export const ALLOWED_DYNAMIC_VARIABLES_ON_RATE_AMOUNT_TEMPLATE = [
  "exchangeCredit",
  "storeCreditAmount",
  "deductedShippingFee",
  "finalizeFlowAdjustment",
  "refundToGiftCardAmount",
] as const;

export const NETSUITE_ACCOUNT_ID_FORMAT_ERROR_MESSAGE =
  "Non-production account IDs must use an uppercase, underscore-prefixed environment suffix: `_SB<number>` for sandbox (e.g. `1234567_SB1`, `1234567_SB2`) or `_RP` / `_RP<number>` for release preview (e.g. `1234567_RP`, `1234567_RP1`).";

const VALID_NETSUITE_ACCOUNT_ID = /^\d+(?:_SB\d+|_RP\d*)?$/;

export function getNetSuiteAccountIdErrorMessage(
  accountId: string,
): string | undefined {
  const trimmed = accountId.trim();
  if (trimmed === "") {
    return undefined;
  }
  if (VALID_NETSUITE_ACCOUNT_ID.test(trimmed)) {
    return undefined;
  }
  return NETSUITE_ACCOUNT_ID_FORMAT_ERROR_MESSAGE;
}
